import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useQuery } from '@tanstack/react-query';
import { filesListFiles, filesDownloadFile, siteGetSites } from '../../api';
import { useIsAdmin, useAffiliatedSiteIds } from '../../lib/auth';
import { DataTabs } from './DataTabs';
import type { SmartField_Api_Contracts_Files_BlobHierarchyItemDto } from '../../api';

export function BrowsePage() {
  const isAdmin = useIsAdmin();
  const siteIds = useAffiliatedSiteIds();
  
  const [currentPath, setCurrentPath] = useState('');
  const [selectedSiteId, setSelectedSiteId] = useState<number | null>(null);
  const [isDownloading, setIsDownloading] = useState(false);

  // Fetch sites
  const { data: sites } = useQuery({
    queryKey: ['sites'],
    queryFn: () => siteGetSites(),
    enabled: isAdmin || siteIds.length > 0,
  });

  // Filter sites based on user access
  const userSites = isAdmin 
    ? sites 
    : sites?.filter(site => site.id && siteIds.includes(site.id));

  // Get selected site
  const selectedSite = userSites?.find(s => s.id === selectedSiteId);
  const containerName = selectedSite?.serialNumber?.toLowerCase();

  // Fetch files for the selected site and current path
  const { data: files = [], isLoading } = useQuery({
    queryKey: ['files', containerName, currentPath],
    queryFn: () => filesListFiles({ 
      containerName: containerName!, 
      path: currentPath || undefined
    }),
    enabled: !!containerName,
  });

  // Auto-select first site if none selected
  if (!selectedSiteId && userSites && userSites.length > 0) {
    const firstSiteId = userSites[0]?.id;
    if (firstSiteId) {
      setSelectedSiteId(firstSiteId);
    }
  }

  const handleDownloadFile = async (blobName: string) => {
    if (!containerName) return;
    
    setIsDownloading(true);
    try {
      const response = await filesDownloadFile({ 
        containerName, 
        blobName 
      });
      
      // Create a download link
      const url = window.URL.createObjectURL(response);
      const link = document.createElement('a');
      link.href = url;
      link.download = blobName.split('/').pop() || 'download';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download failed:', error);
      alert('Failed to download file');
    } finally {
      setIsDownloading(false);
    }
  };


  const navigateToFolder = (folderPath: string) => {
    setCurrentPath(folderPath);
  };

  const navigateUp = () => {
    const pathWithoutTrailingSlash = currentPath.replace(/\/$/, '');
    const lastSlashIndex = pathWithoutTrailingSlash.lastIndexOf('/');
    const parentPath = lastSlashIndex === -1 ? '' : pathWithoutTrailingSlash.substring(0, lastSlashIndex);
    setCurrentPath(parentPath);
  };

  const getFileIcon = (item: SmartField_Api_Contracts_Files_BlobHierarchyItemDto) => {
    if (item.isDirectory) {
      return (
        <svg className="w-6 h-6 text-yellow-500" fill="currentColor" viewBox="0 0 20 20">
          <path d="M2 6a2 2 0 012-2h5l2 2h5a2 2 0 012 2v6a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" />
        </svg>
      );
    }

    // File type icons based on extension
    const fileName = item.blob?.name || '';
    const ext = fileName.split('.').pop()?.toLowerCase();

    switch (ext) {
      case 'csv':
      case 'xlsx':
      case 'xls':
        return (
          <svg className="w-6 h-6 text-green-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 6a1 1 0 011-1h6a1 1 0 110 2H7a1 1 0 01-1-1zm1 3a1 1 0 100 2h6a1 1 0 100-2H7z" clipRule="evenodd" />
          </svg>
        );
      case 'pdf':
        return (
          <svg className="w-6 h-6 text-red-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
      case 'jpg':
      case 'jpeg':
      case 'png':
      case 'gif':
        return (
          <svg className="w-6 h-6 text-purple-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 3a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V5a2 2 0 00-2-2H4zm12 12H4l4-8 3 6 2-4 3 6z" clipRule="evenodd" />
          </svg>
        );
      case 'zip':
      case 'tar':
      case 'gz':
        return (
          <svg className="w-6 h-6 text-orange-600" fill="currentColor" viewBox="0 0 20 20">
            <path d="M4 4a2 2 0 00-2 2v8a2 2 0 002 2h12a2 2 0 002-2V8a2 2 0 00-2-2h-5L9 4H4z" />
          </svg>
        );
      case 'parquet':
        return (
          <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
      default:
        return (
          <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M4 4a2 2 0 012-2h4.586A2 2 0 0112 2.586L15.414 6A2 2 0 0116 7.414V16a2 2 0 01-2 2H6a2 2 0 01-2-2V4z" clipRule="evenodd" />
          </svg>
        );
    }
  };

  const getItemName = (item: SmartField_Api_Contracts_Files_BlobHierarchyItemDto) => {
    if (item.isDirectory) {
      return item.prefix?.replace(/\/$/, '').split('/').pop() || '';
    }
    return item.blob?.name?.split('/').pop() || '';
  };

  if (!userSites || userSites.length === 0) {
    return (
      <div className="space-y-6">
        <h1 className="text-3xl font-bold text-gray-900">Browse Data</h1>
        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <p className="text-yellow-800">You don't have access to any sites. Contact an administrator.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h1 className="text-3xl font-bold text-gray-900">Browse Data</h1>

      <DataTabs />

      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2" />
        
        {/* Site selector */}
        <div className="flex items-center gap-2">
          <label htmlFor="site-select" className="text-sm font-medium text-gray-700">
            Site:
          </label>
          <select
            id="site-select"
            value={selectedSiteId || ''}
            onChange={(e) => {
              setSelectedSiteId(Number(e.target.value));
              setCurrentPath('');
            }}
            className="border border-gray-300 rounded-md px-3 py-2 text-sm focus:ring-2 focus:ring-smartfield focus:border-transparent bg-white text-gray-900"
          >
            {userSites.map(site => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Breadcrumb navigation */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
        <nav className="flex items-center gap-2 text-sm">
          <button
            onClick={() => setCurrentPath('')}
            className="text-smartfield hover:text-smartfield-dark font-medium"
          >
            Root
          </button>
          {currentPath.split('/').filter(Boolean).map((part, index, arr) => {
            const pathUpToHere = arr.slice(0, index + 1).join('/');
            return (
              <div key={index} className="flex items-center gap-2">
                <span className="text-gray-400">/</span>
                <button
                  onClick={() => setCurrentPath(pathUpToHere)}
                  className="text-smartfield hover:text-smartfield-dark font-medium"
                >
                  {part}
                </button>
              </div>
            );
          })}
        </nav>
      </div>

      {/* Go Up button */}
      {currentPath && (
        <div>
          <button
            onClick={navigateUp}
            className="inline-flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-md text-sm font-medium transition-colors"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Go Up
          </button>
        </div>
      )}

      {/* Files table */}
      <div className="bg-white shadow-sm rounded-lg border border-gray-200 overflow-hidden">
        {isLoading ? (
          <div className="p-8 text-center text-gray-500">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-smartfield mb-4"></div>
            <p>Loading files...</p>
          </div>
        ) : files.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 13h6m-3-3v6m-9 1V7a2 2 0 012-2h6l2 2h6a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2z" />
            </svg>
            <p>This folder is empty</p>
          </div>
        ) : (
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Name
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Type
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {files.map((item, index) => (
                <tr key={index} className="hover:bg-gray-50 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center gap-3">
                      {getFileIcon(item)}
                      {item.isDirectory ? (
                        <button
                          onClick={() => navigateToFolder(item.prefix!)}
                          className="text-smartfield hover:text-smartfield-dark font-medium"
                        >
                          {getItemName(item)}
                        </button>
                      ) : (
                        <span className="text-gray-900">{getItemName(item)}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {item.isDirectory ? 'Folder' : 'File'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm">
                    {!item.isDirectory && (
                      <button
                        onClick={() => handleDownloadFile(item.blob!.name)}
                        disabled={isDownloading}
                        className="inline-flex items-center gap-2 px-3 py-1.5 bg-smartfield hover:bg-smartfield-dark text-white rounded-md font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {isDownloading && createPortal(
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 shadow-xl">
            <div className="flex items-center gap-3">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-smartfield"></div>
              <p className="text-gray-700 font-medium">Downloading...</p>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}
